/**
 * 地图 'turtle-inn'（30×22 瓦片 = 960×704 px）。
 * 大海龟旅店与外面连续：没有围墙，只有柱子撑起巨大的龟壳屋顶（overhead 层只画屋檐，屋檐外才下雨：rainShelter）。
 * 门口两只小海龟；店内酒台、桌凳、屋顶中央的木地板夹层（北侧抬高的阁楼）与酒台旁的简陋木板楼梯，楼上客房只做暗示。
 * 瓦片 id 见 INN_TILE。所有纹理 key 都是正式资源 key（`inn-tiles`、`inn-column`…）：资源未到货时
 * 场景在同一个 key 下过程化画出开发占位（innDevTextures.ts），正式资源到货后按 key 直接替换。
 * 正式资源需求见 docs/asset-requests/inn-builder.md。
 */
import type { MapData, MapInteraction, MapLayerData, MapLightData } from '@/core/types/contracts';
import { INN_MAP_ID, INN_NPCS, TILE } from './innCast';

/** 旅店 tileset 的纹理 key（正式资源；缺失时开发占位画在同名 key 下）。 */
export const INN_TILESET = 'inn-tiles';
/** 道具纹理 key（正式资源 id，与资产需求单一致）。 */
export const INN_PROP = {
  column: 'inn-column',
  turtle: 'inn-turtle-small',
  table: 'inn-table',
  stool: 'inn-stool',
  jar: 'inn-jar',
  barrel: 'inn-barrel',
  lantern: 'inn-lantern',
} as const;

export const INN_W = 30;
export const INN_H = 22;

/** 开发用 tileset 帧序号（0 = 空）。 */
export const INN_TILE = {
  NONE: 0,
  SAND: 1,          // 湿沙（屋外）
  SAND_DARK: 2,     // 湿沙深色/泥
  PUDDLE: 3,        // 水洼
  PATH: 4,          // 通往码头的土路
  FLOOR: 5,         // 店内石地
  FLOOR_DARK: 6,    // 店内石地（深）
  PLANK: 7,         // 木地板（阁楼）
  RAIL: 8,          // 阁楼栏杆（前沿）
  COUNTER_TOP: 9,   // 酒台台面
  COUNTER_FRONT: 10,// 酒台正面
  SHELF: 11,        // 酒台后的架子/陶罐
  STAIR: 12,        // 简陋木板楼梯
  DOOR: 13,         // 楼上客房门
  RUG: 14,          // 草席
  EAVE: 15,         // 屋檐（龟壳边缘，overhead）
  EAVE_FRONT: 16,   // 屋檐正面（南侧，2:1 立面）
  EAVE_GAP: 17,     // 门口屋檐缺口（透明较高的檐唇）
  EAVE_CORNER: 18,  // 屋檐转角
  LOFT_SHADOW: 19,  // 阁楼在地面投下的阴影带
} as const;

/** 龟壳屋顶覆盖范围（瓦片，含）。 */
export const ROOF = { x0: 3, y0: 2, x1: 26, y1: 16 };
/** 门口檐廊（瓦片）。 */
export const PORCH = { x0: 11, y0: 17, x1: 18, y1: 17 };
/** 阁楼（屋顶中央的木地板夹层，抬高不可进入）。 */
export const LOFT = { x0: 10, y0: 3, x1: 21, y1: 5 };
/** 酒台（台面在 y=6；y=5 是老板站的过道；y=4 是酒台后的陶罐架）。 */
export const BAR = { x0: 4, y0: 6, x1: 9, y1: 6 };
/** 楼上客房门（阁楼上的 detail 瓦片，暗示住宿）。 */
export const LOFT_DOORS = [13, 16, 19] as const;
/** 楼梯（靠近酒台，从地面 y=9 向北架上阁楼）。 */
export const STAIRS = { x: 12, y0: 6, y1: 8 };

export interface InnProp { key: string; tx: number; ty: number; solid?: boolean; w?: number; h?: number; depthY?: number }

const col = (tx: number, ty: number): InnProp => ({ key: INN_PROP.column, tx, ty, solid: true });
/** 凳子不阻挡；深度略高于凳面，让坐着的人画在凳子前面。 */
const stool = (tx: number, ty: number): InnProp => ({ key: INN_PROP.stool, tx, ty, depthY: ty * TILE + 20 });
/** 灯笼挂在柱子上：与柱子同一脚底，深度 +1 画在柱子前。 */
const lantern = (tx: number, ty: number): InnProp => ({ key: INN_PROP.lantern, tx, ty, depthY: (ty + 1) * TILE + 1 });

/** 场景道具（前景，按脚底 y 排序）。tx/ty 为道具所占瓦片（左上），w/h 为占地瓦片数。 */
export const INN_PROPS: InnProp[] = [
  // 柱子（屋檐内侧的一圈 + 撑阁楼的两根）
  col(4, 7), col(25, 7), col(4, 11), col(25, 11), col(4, 15), col(25, 15), col(10, 15), col(19, 15), col(10, 6), col(21, 6),
  // 门口两只小海龟
  { key: INN_PROP.turtle, tx: 11, ty: 17, solid: true },
  { key: INN_PROP.turtle, tx: 18, ty: 17, solid: true },
  // 桌子（争论桌 A 在 17,11）
  { key: INN_PROP.table, tx: 17, ty: 11, solid: true },
  { key: INN_PROP.table, tx: 7, ty: 9, solid: true },
  { key: INN_PROP.table, tx: 8, ty: 13, solid: true },
  { key: INN_PROP.table, tx: 22, ty: 8, solid: true },
  { key: INN_PROP.table, tx: 23, ty: 13, solid: true },
  { key: INN_PROP.table, tx: 13, ty: 13, solid: true },
  // 凳子（不阻挡：角色坐在上面）
  stool(17, 10), stool(16, 11), stool(18, 11), stool(17, 12),
  stool(6, 9), stool(8, 9),
  stool(8, 12),
  stool(21, 8), stool(22, 9),
  stool(23, 12), stool(22, 13),
  stool(13, 12),
  stool(7, 7), // 酒台边只留一张空凳：barfly a/b 的图集是站着喝的
  // 陶罐、酒缸、木桶
  { key: INN_PROP.jar, tx: 24, ty: 15, solid: true },
  { key: INN_PROP.jar, tx: 5, ty: 14, solid: true },
  { key: INN_PROP.barrel, tx: 24, ty: 6, solid: true },
  { key: INN_PROP.barrel, tx: 23, ty: 6, solid: true },
  { key: INN_PROP.barrel, tx: 5, ty: 15, solid: true },
  // 灯笼（挂在柱子上，前景不阻挡）
  lantern(4, 7), lantern(25, 7), lantern(4, 15), lantern(25, 15), lantern(10, 15), lantern(19, 15), lantern(10, 6), lantern(21, 6), lantern(7, 6),
];

export const INN_LIGHTS: MapLightData[] = [
  { x: 4 * TILE + 16, y: 7 * TILE, radius: 96, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 25 * TILE + 16, y: 7 * TILE, radius: 96, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 4 * TILE + 16, y: 15 * TILE, radius: 96, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 25 * TILE + 16, y: 15 * TILE, radius: 96, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 10 * TILE + 16, y: 15 * TILE, radius: 110, color: '#ffb347', intensity: 0.9, flicker: true },
  { x: 19 * TILE + 16, y: 15 * TILE, radius: 110, color: '#ffb347', intensity: 0.9, flicker: true },
  { x: 10 * TILE + 16, y: 6 * TILE, radius: 100, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 21 * TILE + 16, y: 6 * TILE, radius: 100, color: '#ffb347', intensity: 0.8, flicker: true },
  { x: 7 * TILE + 16, y: 6 * TILE, radius: 120, color: '#ffc870', intensity: 1.0, flicker: true },
  { x: 17 * TILE + 16, y: 10 * TILE, radius: 90, color: '#ffb347', intensity: 0.7, flicker: true },
];

const rect = (tx: number, ty: number, w: number, h: number) => ({ x: tx * TILE, y: ty * TILE, w: w * TILE, h: h * TILE });

export const INN_INTERACTIONS: MapInteraction[] = [
  // 主线：坐到空位上（桌东）。落座后进入争论与不祥预感。
  { id: 'inn-seat', ...rect(18, 10, 2, 3), kind: 'trigger', cinematic: 'inn-sit-down', label: '坐下', untilFlag: 'OMEN_END' },
  // 可选：观察
  { id: 'inn-turtle-west', ...rect(10, 17, 2, 2), kind: 'observe', dialogue: 'inn-obs-turtles', label: '看' },
  { id: 'inn-turtle-east', ...rect(18, 17, 2, 2), kind: 'observe', dialogue: 'inn-obs-turtles', label: '看' },
  { id: 'inn-stairs', ...rect(11, 9, 3, 1), kind: 'observe', dialogue: 'inn-obs-stairs', label: '看' },
  { id: 'inn-bar-look', ...rect(4, 7, 1, 1), kind: 'observe', dialogue: 'inn-obs-bar', label: '看' },
  { id: 'inn-roof-look', ...rect(15, 12, 2, 2), kind: 'observe', dialogue: 'inn-obs-roof-inside', label: '抬头看' },
  // 自动一次：挤进店里时
  { id: 'inn-crowd-auto', ...rect(11, 15, 8, 1), kind: 'observe', dialogue: 'inn-obs-crowd', once: true, auto: true, untilFlag: 'INN_ARGUMENT' },
  // 可选：交谈
  { id: 'inn-talk-boss', ...rect(6, 7, 3, 1), kind: 'talk', dialogue: 'inn-talk-boss', label: '说话' },
  { id: 'inn-talk-girls', ...rect(13, 18, 2, 1), kind: 'talk', dialogue: 'inn-talk-girls', label: '说话' },
  { id: 'inn-talk-elders', ...rect(16, 18, 2, 1), kind: 'talk', dialogue: 'inn-talk-elders', label: '说话' },
  { id: 'inn-talk-barfly', ...rect(5, 8, 1, 1), kind: 'talk', dialogue: 'inn-talk-barfly', label: '说话' },
  { id: 'inn-talk-drinker', ...rect(6, 10, 1, 1), kind: 'talk', dialogue: 'inn-talk-drinker', label: '说话' },
  { id: 'inn-talk-sailor', ...rect(9, 14, 1, 1), kind: 'talk', dialogue: 'inn-talk-sailor', label: '说话' },
  { id: 'inn-talk-table', ...rect(16, 9, 2, 1), kind: 'talk', dialogue: 'inn-talk-table', label: '说话', untilFlag: 'INN_ARGUMENT' },
];

function fill(layer: number[], tx: number, ty: number, w: number, h: number, id: number): void {
  for (let y = ty; y < ty + h; y++) for (let x = tx; x < tx + w; x++) if (x >= 0 && y >= 0 && x < INN_W && y < INN_H) layer[y * INN_W + x] = id;
}
/** 小型确定性哈希（用于水洼/深色地砖的散布，不依赖运行时 RNG）。 */
function hash2(x: number, y: number): number {
  let h = (x * 374761393 + y * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

export function buildInnMap(): MapData {
  const T = INN_TILE;
  const ground = new Array<number>(INN_W * INN_H).fill(T.SAND);
  const detail = new Array<number>(INN_W * INN_H).fill(T.NONE);
  const overhead = new Array<number>(INN_W * INN_H).fill(T.NONE);
  const collision = new Array<number>(INN_W * INN_H).fill(0);

  // 屋外：湿沙、泥、水洼、通往码头的土路
  for (let y = 0; y < INN_H; y++) for (let x = 0; x < INN_W; x++) {
    const r = hash2(x, y);
    if (r < 0.12) ground[y * INN_W + x] = T.SAND_DARK;
    else if (r > 0.9) ground[y * INN_W + x] = T.PUDDLE;
  }
  fill(ground, 13, 17, 4, 5, T.PATH);
  fill(ground, 12, 19, 6, 3, T.PATH);

  // 屋内石地（屋顶覆盖范围）+ 深浅变化
  for (let y = ROOF.y0; y <= ROOF.y1; y++) for (let x = ROOF.x0; x <= ROOF.x1; x++) {
    ground[y * INN_W + x] = hash2(x + 7, y + 3) < 0.22 ? T.FLOOR_DARK : T.FLOOR;
  }
  // 檐廊
  fill(ground, PORCH.x0, PORCH.y0, PORCH.x1 - PORCH.x0 + 1, 1, T.FLOOR);
  // 阁楼木地板（抬高）+ 前沿栏杆 + 阁楼阴影
  fill(ground, LOFT.x0, LOFT.y0, LOFT.x1 - LOFT.x0 + 1, LOFT.y1 - LOFT.y0 + 1, T.PLANK);
  fill(detail, LOFT.x0, LOFT.y1, LOFT.x1 - LOFT.x0 + 1, 1, T.RAIL);
  fill(detail, LOFT.x0, LOFT.y1 + 1, LOFT.x1 - LOFT.x0 + 1, 1, T.LOFT_SHADOW);
  // 楼梯口不画栏杆
  detail[LOFT.y1 * INN_W + STAIRS.x] = T.NONE;
  // 酒台：陶罐架在后（y=4）、老板的过道（y=5，深色石地）、台面（y=6）
  fill(detail, BAR.x0, 4, BAR.x1 - BAR.x0 + 1, 1, T.SHELF);
  fill(ground, BAR.x0, 5, BAR.x1 - BAR.x0 + 1, 1, T.FLOOR_DARK);
  fill(detail, BAR.x0, BAR.y0, BAR.x1 - BAR.x0 + 1, 1, T.COUNTER_TOP);
  // 楼梯
  fill(detail, STAIRS.x, STAIRS.y0, 1, STAIRS.y1 - STAIRS.y0 + 1, T.STAIR);
  // 草席
  fill(detail, 16, 10, 3, 3, T.RUG);
  fill(detail, 21, 12, 3, 3, T.RUG);
  fill(detail, 6, 8, 3, 3, T.RUG);
  // 楼上客房门（阁楼上，暗示住宿）
  for (const tx of LOFT_DOORS) detail[3 * INN_W + tx] = T.DOOR;

  // 屋檐（overhead）：一圈龟壳边缘，南侧为立面；门口留缺口（檐唇）
  for (let x = ROOF.x0; x <= ROOF.x1; x++) {
    overhead[ROOF.y0 * INN_W + x] = T.EAVE;
    overhead[ROOF.y1 * INN_W + x] = x >= PORCH.x0 + 1 && x <= PORCH.x1 - 1 ? T.EAVE_GAP : T.EAVE_FRONT;
  }
  for (let y = ROOF.y0; y <= ROOF.y1; y++) {
    overhead[y * INN_W + ROOF.x0] = T.EAVE;
    overhead[y * INN_W + ROOF.x1] = T.EAVE;
  }
  overhead[ROOF.y0 * INN_W + ROOF.x0] = T.EAVE_CORNER;
  overhead[ROOF.y0 * INN_W + ROOF.x1] = T.EAVE_CORNER;
  overhead[ROOF.y1 * INN_W + ROOF.x0] = T.EAVE_CORNER;
  overhead[ROOF.y1 * INN_W + ROOF.x1] = T.EAVE_CORNER;

  // 碰撞：地图最北两行、屋檐北沿、阁楼、酒台台面与陶罐架（老板的过道 y=5 保持可走，但从西侧封死）、楼梯、道具
  fill(collision, 0, 0, INN_W, ROOF.y0 + 1, 1);
  fill(collision, LOFT.x0, LOFT.y0, LOFT.x1 - LOFT.x0 + 1, LOFT.y1 - LOFT.y0 + 1, 1);
  fill(collision, BAR.x0, 4, BAR.x1 - BAR.x0 + 1, 1, 1);
  fill(collision, BAR.x0, BAR.y0, BAR.x1 - BAR.x0 + 1, 1, 1);
  fill(collision, BAR.x0 - 1, 4, 1, 2, 1);
  fill(collision, STAIRS.x, STAIRS.y0, 1, STAIRS.y1 - STAIRS.y0 + 1, 1);
  for (const p of INN_PROPS) if (p.solid) fill(collision, p.tx, p.ty, p.w ?? 1, p.h ?? 1, 1);
  // 地图左右最外一列与最底一行保持可走，出口在底边

  const layers: MapLayerData[] = [
    { name: 'ground', tileset: INN_TILESET, data: ground, depth: 'ground' },
    { name: 'detail', tileset: INN_TILESET, data: detail, depth: 'detail' },
    { name: 'overhead', tileset: INN_TILESET, data: overhead, depth: 'overhead' },
  ];

  return {
    id: INN_MAP_ID,
    width: INN_W,
    height: INN_H,
    tileSize: 32,
    tilesets: [{ id: INN_TILESET, texture: INN_TILESET, tileWidth: TILE, tileHeight: TILE }],
    layers,
    collision,
    foreground: INN_PROPS.map((p) => ({ key: p.key, x: p.tx * TILE + 16, y: (p.ty + (p.h ?? 1)) * TILE, depthY: p.depthY })),
    exits: [
      { id: 'to-harbor', ...rect(11, 21, 8, 1), target: 'green-harbor', spawn: 'inn-approach' },
    ],
    spawns: [
      { id: 'inn-entrance', x: 14 * TILE + 16, y: 19 * TILE + 28, facing: 'up' },
      { id: 'stub', x: 14 * TILE + 16, y: 19 * TILE + 28, facing: 'up' },
      { id: 'inn-porch', x: 15 * TILE + 16, y: 16 * TILE + 28, facing: 'up' },
      { id: 'inn-seat-approach', x: 19 * TILE + 16, y: 11 * TILE + 28, facing: 'left' },
    ],
    interactions: INN_INTERACTIONS,
    npcs: INN_NPCS,
    lights: INN_LIGHTS,
    rainShelter: [
      { x: ROOF.x0 * TILE, y: ROOF.y0 * TILE, w: (ROOF.x1 - ROOF.x0 + 1) * TILE, h: (ROOF.y1 - ROOF.y0 + 1) * TILE },
      { x: PORCH.x0 * TILE, y: PORCH.y0 * TILE, w: (PORCH.x1 - PORCH.x0 + 1) * TILE, h: TILE },
    ],
    music: 'inn-rain',
    ambience: ['rain-on-shell', 'inn-crowd'],
  };
}

export const INN_MAP: MapData = buildInnMap();

/** 某像素点是否在龟壳屋顶（或檐廊）之下 —— 雨只下在外面。 */
export function isSheltered(px: number, py: number, map: MapData = INN_MAP): boolean {
  return (map.rainShelter ?? []).some((r) => px >= r.x && px < r.x + r.w && py >= r.y && py < r.y + r.h);
}
